import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiGet, apiPost } from "../lib/api";
import { COLORS } from "../lib/colors";

function formatTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
];

export default function RequestsTab({ onPendingCount }) {
  const { candidate, session } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const loadRequests = useCallback(async () => {
    if (!candidate?.id || !session?.access_token) return;
    try {
      const data = await apiGet(`/cv-requests/${candidate.id}`, session.access_token);
      setRequests(data.requests || []);
      const pending = (data.requests || []).filter((r) => r.status === "pending").length;
      onPendingCount?.(pending);
    } catch (err) {
      console.error("Failed to load requests:", err);
    } finally {
      setLoading(false);
    }
  }, [candidate?.id, session?.access_token, onPendingCount]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  const respondToRequest = async (requestId, action) => {
    try {
      await apiPost(
        `/cv-requests/${requestId}/respond?action=${action}`,
        null,
        session.access_token
      );

      // Update local state
      setRequests((prev) => {
        const updated = prev.map((r) =>
          r.id === requestId ? { ...r, status: action } : r
        );
        const pending = updated.filter((r) => r.status === "pending").length;
        onPendingCount?.(pending);
        return updated;
      });

      const actionText =
        action === "approved" ? "CV request approved" :
        action === "declined" ? "CV request declined" : "Access revoked";
      showToast(actionText);
    } catch (err) {
      console.error("Respond error:", err);
      showToast("Something went wrong. Try again.");
    }
  };

  const filtered = filter === "all" ? requests : requests.filter((r) => r.status === filter);

  return (
    <div>
      {/* Header */}
      <div style={{ padding: "20px 20px 4px" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text }}>
          CV Requests
        </h2>
        <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>
          Manage who can access your CV
        </p>
      </div>

      {/* Filter tabs */}
      <div style={{
        display: "flex", gap: 8, padding: "14px 20px", overflowX: "auto",
      }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: "7px 16px",
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 500,
              fontFamily: "inherit",
              border: `1px solid ${filter === f.key ? COLORS.accent : COLORS.border}`,
              backgroundColor: filter === f.key ? COLORS.accent : "transparent",
              color: filter === f.key ? "#fff" : COLORS.textSecondary,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Request list */}
      <div style={{ padding: "0 20px" }}>
        {loading ? (
          <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", padding: 20 }}>
            Loading...
          </p>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 20px" }}>
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.5 }}>&#128237;</div>
            <p style={{ fontSize: 13, color: COLORS.textSecondary }}>
              No {filter === "all" ? "" : filter} requests yet.
            </p>
          </div>
        ) : (
          filtered.map((r) => {
            const company = r.recruiter?.company || "Unknown";
            const logo = company.substring(0, 2).toUpperCase();
            const recruiterName = r.recruiter?.name || "Recruiter";
            const isRecruiterInactive = r.recruiter?.status === "revoked";
            const scorePercent = r.match_score ? Math.round(r.match_score * 100) : null;
            const scoreColor = r.match_score >= 0.85 ? COLORS.green
              : r.match_score >= 0.7 ? COLORS.orange : COLORS.textMuted;

            return (
              <div
                key={r.id}
                style={{
                  backgroundColor: COLORS.surface,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 12,
                }}
              >
                {/* Top row */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    backgroundColor: COLORS.surfaceAlt,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700, color: COLORS.textSecondary, flexShrink: 0,
                  }}>
                    {logo}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>
                      {company}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, display: "flex", alignItems: "center", gap: 6 }}>
                      {recruiterName}
                      {isRecruiterInactive && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: COLORS.textMuted,
                          backgroundColor: COLORS.surfaceAlt, padding: "1px 6px",
                          borderRadius: 4, lineHeight: "16px",
                        }}>
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Meta */}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                  {r.search_query && (
                    <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
                      Searched: <strong>{r.search_query}</strong>
                    </span>
                  )}
                  {scorePercent !== null && (
                    <span style={{
                      fontSize: 12, fontWeight: 600, color: scoreColor,
                      backgroundColor: scoreColor === COLORS.green ? COLORS.greenLight
                        : scoreColor === COLORS.orange ? COLORS.orangeLight : COLORS.surfaceAlt,
                      padding: "2px 8px", borderRadius: 4,
                    }}>
                      {scorePercent}% match
                    </span>
                  )}
                </div>

                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>
                  {formatTime(r.requested_at)}
                </div>

                {/* Actions */}
                {r.status === "pending" && !isRecruiterInactive && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => respondToRequest(r.id, "declined")}
                      style={{
                        flex: 1, padding: "9px 0", borderRadius: 8,
                        border: `1px solid ${COLORS.border}`,
                        backgroundColor: "transparent",
                        color: COLORS.textSecondary,
                        fontSize: 13, fontWeight: 500, fontFamily: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => respondToRequest(r.id, "approved")}
                      style={{
                        flex: 1, padding: "9px 0", borderRadius: 8,
                        border: "none",
                        backgroundColor: COLORS.accent,
                        color: "#fff",
                        fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      Approve
                    </button>
                  </div>
                )}

                {r.status === "pending" && isRecruiterInactive && (
                  <span style={{
                    fontSize: 12, fontWeight: 500, color: COLORS.textMuted,
                    backgroundColor: COLORS.surfaceAlt,
                    padding: "4px 10px", borderRadius: 4,
                  }}>
                    Recruiter no longer active
                  </span>
                )}

                {r.status === "approved" && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <button
                      onClick={() => respondToRequest(r.id, "revoked")}
                      style={{
                        padding: "8px 16px", borderRadius: 8,
                        border: `1px solid ${COLORS.border}`,
                        backgroundColor: "transparent",
                        color: COLORS.textSecondary,
                        fontSize: 12, fontFamily: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      Revoke access
                    </button>
                    <span style={{
                      fontSize: 12, fontWeight: 600, color: COLORS.green,
                      backgroundColor: COLORS.greenLight,
                      padding: "4px 10px", borderRadius: 4,
                    }}>
                      Approved
                    </span>
                  </div>
                )}

                {r.status === "declined" && (
                  <span style={{
                    fontSize: 12, fontWeight: 500, color: COLORS.red,
                    backgroundColor: COLORS.redLight,
                    padding: "4px 10px", borderRadius: 4,
                  }}>
                    Declined
                  </span>
                )}

                {r.status === "revoked" && (
                  <span style={{
                    fontSize: 12, fontWeight: 500, color: COLORS.textMuted,
                    backgroundColor: COLORS.surfaceAlt,
                    padding: "4px 10px", borderRadius: 4,
                  }}>
                    Revoked
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 90,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: COLORS.text,
          color: "#fff",
          padding: "10px 20px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 500,
          zIndex: 200,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
